// @ts-check

import store from '@/store';
import { post } from '@/utils/api';
import { Brands } from 'acb-lib';
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router/composables';

/**
 * Replaces dates with strings
 * @typedef {import('acb-lib').TypeUtils.ToFrontEnd<T>} ToFrontEnd
 * @template T
 */

/**
 * @typedef {import('acb-lib').Brands.Brand} Brand
 * @typedef {import('acb-lib').Brands.BrandSettings} BrandSettings
 * @typedef {import('acb-lib').Brands.BrandImageType} BrandImageType
 * @typedef {import('acb-lib').Theme.GetThemeStyleEndpointResSuccess} ResSuccess
 * @typedef {import('acb-lib').Theme.GetThemeStyleEndpointResSuccess} ResError
 * @typedef {import('axios').AxiosError<ResError>} ResErrorAxios
 */

export function useBrandIdInUrl()
{
	const route = useRoute();

	const paramsUrlBrandId = computed(() =>
	{
		return +route.params.brandId;
	});

	return { paramsUrlBrandId };
}

export function useActiveBrand()
{
	const activeBrandId = computed(() =>
	{
		return store.getters['app/brands/activeBrandId'];
	});

	const {
		brand,
		brandId,
		brandSettings,
		faviconId,
		logoId
	} = useBrand(activeBrandId);

	return {
		activeBrand: brand,
		activeBrandId: brandId,
		activeBrandSettings: brandSettings,
		activeBrandFaviconId: faviconId,
		activeBrandLogoId: logoId
	};
}

export function useBrands()
{
	const activatingBrand = ref(false);

	const themeModeEnabled = computed(() =>
	{
		return store.getters['app/settings/themeModeEnabled'];
	});

	async function saveThemeMode()
	{
		await store.dispatch('app/settings/changeSetting', {
			name: 'themeMode',
			changes: { enabled: !themeModeEnabled.value },
			merge: false
		});
	}

	/**
	 * @param {number} brandId
	 */
	async function loadBrandSettingsByBrandId(brandId)
	{
		await store.dispatch('app/brands/loadBrandSettingsByBrandId', { brandId });
	}

	/**
	 * @param {object} ctx
	 * @param {boolean=} ctx.force
	 */
	async function loadAllBrandSettings(ctx)
	{
		await store.dispatch(
			'app/brands/loadAllBrandSettingsByBrandId',
			ctx
		);
	}

	/**
	 * @param {object} ctx
	 * @param {boolean=} ctx.force
	 */
	async function loadEverything(ctx)
	{
		await loadAllBrandSettings(ctx);
		await loadAllBrandThemeStyles(ctx);
	}

	/**
	 * @param {object} ctx
	 * @param {boolean=} ctx.force
	 */
	async function loadAllBrandThemeStyles(ctx)
	{
		await store.dispatch(
			'app/brands/loadAllBrandThemeStylesByBrandId',
			ctx
		);
	}

	/**
	 * @param {number} brandId
	 * @returns {BrandSettings | undefined}
	 */
	function getBrandSettingsByBrandId(brandId)
	{
		return store.getters['app/brands/getBrandSettingsByBrandId'](brandId);
	}

	/**
	 * @param {number} brandId
	 * @returns {ToFrontEnd<Brand> | undefined}
	 */
	function getBrandById(brandId)
	{
		return store.getters['app/brands/getBrandById'](brandId);
	}

	/**
	 * @param {number} brandId
	 * @returns {string | undefined}
	 */
	function getBrandNameById(brandId)
	{
		return brands.value.find((brand) => brand.brandId === brandId)?.brandName;
	}

	/**
	 * @param {number} brandId
	 */
	async function setBrandAsActive(
		brandId
	)
	{
		activatingBrand.value = true;

		await store.dispatch(
			'app/brands/setActiveBrand',
			{
				brandId
			}
		);

		activatingBrand.value = false;
	}

	/**
	 * @param {number} brandId
	 * @param {string} brandName
	 */
	async function updateBrandName(
		brandId,
		brandName
	)
	{
		await store.dispatch(
			'app/brands/updateBrandName',
			{
				brandId,
				brandName
			}
		);
	}

	const brands = computed(() =>
	{
		return /** @type {ToFrontEnd<Brand>[]} */ (
			store.getters['app/brands/all']
		);
	});

	/**
	 * @param {number} brandId
	 */
	function getEditBrandUrl(brandId)
	{
		return {
			name: 'editBrand',
			params: {
				// string required as it's going into the URL
				// (or TS will complain)
				brandId: `${brandId}`
			}
		};
	}

	/**
	 * @param {number} brandId
	 */
	async function previewBrand(brandId)
	{
		await store.dispatch(
			'app/brands/setPreviewingBrandId',
			brandId
		);
	}

	/**
	 * @returns {Promise<void | Error>}
	 */
	async function loadBrands()
	{
		return store.dispatch('app/brands/loadAndSetBrands');
	}

	/**
	 * @param {object} ctx
	 * @param {number} ctx.brandId
	 * @returns {Promise<void | Error>}
	 */
	async function loadOneBrandById({ brandId })
	{
		return store.dispatch('app/brands/loadAndSetOneBrand', { brandId });
	}

	/**
	 *
	 * @param {BrandImageType} brandImageType
	 * @param {number} brandId
	 * @param {string} image
	 */
	async function setBrandImage(brandImageType, brandId, image)
	{
		const brandImageTypeParsed = Brands.cBrandImageTypeSchema
			.safeParse(brandImageType);

		if(brandImageTypeParsed.success === false)
		{
			// ERROR

			return;
		}

		const { data } = await post(
			`service/brands/${brandId}/${brandImageTypeParsed.data}`,
			{ changes: { image } }
		);

		/** @satisfies {Record<BrandImageType, keyof BrandSettings>} */
		const brandSettingKeyByBrandImageType = {
			favicon: 'faviconId',
			logo: 'logoId'
		};

		store.dispatch(
			'app/brands/setBrandSetting',
			{
				brandId,
				brandSettingKey: brandSettingKeyByBrandImageType[brandImageType],
				brandSettingValue: data.id
			}
		);
	}

	/**
	 * @param {object} ctx
	 * @param {string} ctx.image
	 * @param {number} ctx.brandId
	 */
	async function setBrandLogo(ctx)
	{
		await setBrandImage('logo', ctx.brandId, ctx.image);
	}

	/**
	 * @param {object} ctx
	 * @param {string} ctx.image
	 * @param {number} ctx.brandId
	 */
	async function setBrandFavicon(ctx)
	{
		await setBrandImage('favicon', ctx.brandId, ctx.image);
	}

	const previewingBrandWithId = computed(() =>
	{
		return store.getters['app/brands/previewingBrandWithId'];
	});

	async function resetPreviewingBrand()
	{
		await store.dispatch('app/brands/resetBrandPreview');
	}

	/**
	 * @param {boolean} required
	 */
	async function setBlankThemeRequired(required)
	{
		await store.dispatch('app/brands/setBlankThemeRequired', required);
	}

	/**
	 * @param {object} ctx
	 * @param {string} ctx.brandName
	 */
	async function createNewBrand({
		brandName
	})
	{
		const { data } = await post('service/brands', { brandName });

		return data;
	}

	return {
		brands,
		activatingBrand,
		themeModeEnabled,
		previewingBrandWithId,
		loadBrands,
		getBrandById,
		setBrandLogo,
		previewBrand,
		saveThemeMode,
		loadEverything,
		createNewBrand,
		getEditBrandUrl,
		updateBrandName,
		setBrandFavicon,
		setBrandAsActive,
		loadOneBrandById,
		getBrandNameById,
		resetPreviewingBrand,
		loadAllBrandSettings,
		loadAllBrandThemeStyles,
		setBlankThemeRequired,
		getBrandSettingsByBrandId,
		loadBrandSettingsByBrandId
	};
}

/**
 *
 * @param {import('vue').Ref<number>} brandId
 * @returns
 */
export function useBrand(brandId)
{
	const { getBrandSettingsByBrandId } = useBrands();

	const brand = computed(() =>
	{
		return /** @type {ToFrontEnd<Brand> | undefined} */ (
			store.getters['app/brands/getBrandById'](brandId.value)
		);
	});

	const brandSettings = computed(() =>
	{
		return getBrandSettingsByBrandId(brandId.value);
	});

	const faviconId = computed(() =>
	{
		return brandSettings.value?.faviconId;
	});

	const logoId = computed(() =>
	{
		return brandSettings.value?.logoId;
	});

	return {
		brand,
		brandId,
		logoId,
		faviconId,
		brandSettings
	};
}

